import {
  CreateButton,
  DateField,
  DeleteButton,
  EditButton,
  ExportButton,
  FilterDropdown,
  getDefaultSortOrder,
  List,
  ShowButton,
  TextField,
  useSelect,
  useTable,
} from "@refinedev/antd";

import { Divider, Select, Space, Table, Tag } from "antd";
import type { ColumnsType } from "antd/es/table";
import { IResourceComponentsProps, useExport } from "@refinedev/core";
import { ILicence, IMetadataAvcs, IVessel } from "interfaces";
import React from "react";
import { formatMoney } from "components/metadata/formatMoney";

export const MetadataAvcsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorters, tableQuery: { isLoading } } = useTable<IMetadataAvcs>({
    sorters: {
      initial: [
        {
          field: "id",
          order: "desc",
        },
      ]
    }
  });

  const { triggerExport, isLoading: isLoadingExport } =
    useExport<IMetadataAvcs>({
      mapData: (item) => {
        return {
          "UKHO Licence ID": item?.subscription?.licence?.ukhoLicenceId || "",
          "Product Name": item?.productName || "",
          "Product Group": item?.productGroup || "",
          "Currency": item?.currency || "",
          "Subscription Type": item?.subscriptionType || "",
          "Payment Frequency": item?.paymentFrequency || "",
          "Discount Percentage": item?.discountPercentage || "",
          "Annual Flat Fee Per Vessel":
            item?.annualFlatFeePerVessel == null
              ? ""
              : formatMoney(null, item.annualFlatFeePerVessel),
          "Activation Status": item?.activationStatus || "",
          "Licence Type": item?.licenceType || "",
          "Expiry Date": item?.expiryDate || "",
          "Master Permit": item?.masterPermit || "",
          "Master Permit Version": item?.masterPermitVersion || "",
          "Backup Permit": item?.backupPermit || "",
          "Backup Permit Version": item?.backupPermitVersion || "",
          "Reserve 1 Permit": item?.reserve1Permit || "",
          "Reserve 1 Permit Version": item?.reserve1PermitVersion || "",
          "Reserve 2 Permit": item?.reserve2Permit || "",
          "Reserve 2 Permit Version": item?.reserve2PermitVersion || "",
          "Reserve 3 Permit": item?.reserve3Permit || "",
          "Reserve 3 Permit Version": item?.reserve3PermitVersion || "",
        };
      },
      unparseConfig: {
        header: true,
        columns: [
          "UKHO Licence ID",
          "Product Name",
          "Product Group",
          "Currency",
          "Subscription Type",
          "Payment Frequency",
          "Discount Percentage",
          "Annual Flat Fee Per Vessel",
          "Activation Status",
          "Licence Type",
          "Expiry Date",
          "Master Permit",
          "Master Permit Version",
          "Backup Permit",
          "Backup Permit Version",
          "Reserve 1 Permit",
          "Reserve 1 Permit Version",
          "Reserve 2 Permit",
          "Reserve 2 Permit Version",
          "Reserve 3 Permit",
          "Reserve 3 Permit Version",
        ],
      },
    });

  const { selectProps: licenceSelectProps } = useSelect<ILicence>({
    resource: "licence",
    optionLabel: "ukhoLicenceId",
    optionValue: "ukhoLicenceId",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: imoSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "imoNumber",
    optionValue: "imoNumber",
    pagination: {
      mode: "server"
    }
  });

  const { selectProps: vesselNameSelectProps } = useSelect<IVessel>({
    resource: "vessel",
    optionLabel: "name",
    optionValue: "name",
    pagination: {
      mode: "server"
    }
  });

  const columns: ColumnsType<IMetadataAvcs> = [
    {
      title: "UKHO Licence",
      dataIndex: ["subscription", "licence", "ukhoLicenceId"],
      key: "subscription.licence.ukhoLicenceId",
      width: 150,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Licence"
            {...licenceSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "IMO Number",
      dataIndex: ["subscription", "licence", "vessel", "imoNumber"],
      key: "subscription.licence.vessel.imoNumber",
      width: 130,
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select IMO Number"
            {...imoSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Vessel Name",
      dataIndex: ["subscription", "licence", "vessel", "name"],
      key: "subscription.licence.vessel.name",
      width: 180,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      filterDropdown: (props) => (
        <FilterDropdown {...props}>
          <Select
            style={{ minWidth: 200 }}
            mode="multiple"
            placeholder="Select Vessel"
            {...vesselNameSelectProps}
          />
        </FilterDropdown>
      ),
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      width: 140,
      render: (value) => <DateField format="DD-MMM-YYYY" value={value} />,
      defaultSortOrder: getDefaultSortOrder("expiryDate", sorters),
      sorter: true,
    },
    {
      title: "Master Permit",
      dataIndex: "masterPermit",
      key: "masterPermit",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("masterPermit", sorters),
      sorter: true,
    },
    {
      title: "Backup Permit",
      dataIndex: "backupPermit",
      key: "backupPermit",
      width: 200,
      className: "wrap-text",
      render: (value) => <TextField value={value} />,
      defaultSortOrder: getDefaultSortOrder("backupPermit", sorters),
      sorter: true,
    },
    {
      title: "Actions",
      key: "actions",
      fixed: "right",
      width: 120,
      render: (_, record) => (
        <Space>
          <EditButton hideText size="small" recordItemId={record.id} />
          <ShowButton hideText size="small" recordItemId={record.id} />
          <DeleteButton hideText size="small" recordItemId={record.id} />
        </Space>
      ),
    },
  ];

  return (
    <List
      breadcrumb={false}
      headerProps={{
        // onBack: () => window.history.back(),
        title: "AVCS Service",
        subTitle: "active AVCS services",
        tags: isLoading ? (
          <Tag color="orange">loading</Tag>
        ) : (
          <Tag color="green">live</Tag>
        ),
        extra: (
          <React.Fragment>
            <Space>
              <CreateButton />
              <ExportButton onClick={triggerExport} loading={isLoadingExport} />
            </Space>
          </React.Fragment>
        ),
        ghost: true,
        footer: (
          <Divider plain>
            © {new Date().getFullYear()} e-Mariner NavSolutions
          </Divider>
        ),
      }}
    >
      <Table
        {...tableProps}
        columns={columns}
        rowKey="id"
        size="middle"
        scroll={{ x: 1200 }}
        sticky
        pagination={{
          ...tableProps.pagination,
          showSizeChanger: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
        }}
      />
    </List>
  );
};
